import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { FaArrowLeft } from 'react-icons/fa'
import { authService } from '../services'
import { useTranslation } from 'react-i18next'
import { usePageTitle, setDocumentTitle } from '../utils/translation'

export default function ResetPassword() {
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [token, setToken] = useState<string | null>(null)
  const [showRequired, setShowRequired] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // Get translated page title
  const pageTitle = usePageTitle('resetPassword.pageTitle', 'Reset Password')

  useEffect(() => {
    // Set document title
    setDocumentTitle(pageTitle)
    
    const tokenParam = searchParams.get('token')
    if (tokenParam) {
      setToken(tokenParam)
    } else {
      setError(t('resetPassword.errors.invalidToken'))
    }
  }, [searchParams, pageTitle, t])

  const handleBack = () => {
    // Asking for confirmation before going back to prevent accidental navigation
    if (window.confirm(t('resetPassword.confirmLeavePrompt'))) {
      navigate('/login')
    }
  }

  const validatePassword = (password: string): boolean => {
    if (password.length < 8) {
      setError(t('resetPassword.errors.passwordTooShort'))
      return false
    }
    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate inputs
    if (!password || !confirmPassword) {
      setShowRequired(true)
      return
    }
    
    // Validate password length
    if (!validatePassword(password)) {
      return
    }
    
    // Check passwords match
    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordsDoNotMatch'))
      return
    }
    
    if (!token) {
      setError(t('resetPassword.errors.tokenMissing'))
      return
    }
    
    setLoading(true)
    setError('')
    
    try {
      // Reset the password using the token
      const response = await authService.resetPassword(token, password)
      
      if (response.success) {
        setSuccess(true)
      } else {
        setError(response.message || t('resetPassword.errors.resetFailed'))
      }
    } catch (err: any) {
      console.error('Password reset error:', err)
      
      // Extract error message from the response if available
      let errorMessage = t('resetPassword.errors.resetFailed')
      
      if (err.response) {
        const responseError = err.response.data?.error
        
        if (responseError) {
          errorMessage = responseError
        }
      } else if (err.message) {
        errorMessage = err.message
      }
      
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="p-6 flex-1">
        {/* Navbar with back button - only show if user is successfully resetting password or wants to go back */}
        <div className="flex items-center mb-8">
          <button onClick={handleBack} className="mr-4 p-2 bg-gray-200 rounded-full">
            <FaArrowLeft className="text-gray-700 text-xl font-bold" />
          </button>
        </div>

        <h2 className="text-2xl font-semibold mb-2">{t('resetPassword.pageTitle')}</h2>

        <p className="text-gray-700 text-sm mb-6">
          {t('resetPassword.instructions')}
        </p>

        {success ? (
          <div className="bg-green-50 p-4 rounded-[16px] mb-6">
            <p className="text-green-700 font-medium">{t('resetPassword.success.title')}</p>
            <p className="text-green-600 text-sm mt-1">
              {t('resetPassword.success.message')}
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="w-full h-[60px] rounded-[16px] font-medium text-black mt-6 bg-[#F9D348]"
            >
              {t('resetPassword.buttons.goToLogin')}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col h-full">
            <input
              type="password"
              placeholder={t('resetPassword.form.newPasswordPlaceholder')}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
                setShowRequired(false)
                setError('')
              }}
              className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
              autoFocus
            />
            
            <input
              type="password"
              placeholder={t('resetPassword.form.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
                setShowRequired(false)
                setError('')
              }}
              className="w-full p-4 border border-gray-300 rounded-[16px] mb-4 focus:outline-none focus:border-gray-500"
            />
            
            {showRequired && (
              <span className="text-red-500 text-sm mb-4">
                {t('resetPassword.errors.allFieldsRequired')}
              </span>
            )}
            
            {error && (
              <span className="text-red-500 text-sm mb-4">{error}</span>
            )}

            <button 
              type="submit"
              disabled={loading || !token}
              className={`w-full h-[60px] rounded-[16px] font-medium text-black mt-6 ${
                password && confirmPassword && token ? 'bg-[#F9D348]' : 'bg-[#FDEDB6]'
              }`}
            >
              {loading ? t('resetPassword.buttons.processing') : t('resetPassword.buttons.resetPassword')}
            </button>
          </form>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm">
            {t('resetPassword.login.rememberPassword')}{' '}
            <Link to="/login" className="text-blue-600 font-medium">
              {t('resetPassword.login.loginLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
} 